import { Link } from "react-router-dom";
import { useEffect } from "react";
import { track } from "@/lib/track";
import FaqList from "@/components/FaqList";
import { PATHWAYS, TENK30_FAQ } from "@/lib/longevity";
import { Footprints, Dumbbell, Utensils, Moon, BookOpen, ArrowRight, Check, X } from "lucide-react";

const IMG_BANNER   = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/5vfphy6s_7160A4B6-6B41-4D3F-BAEC-0FA13467B36D.png";
const IMG_WALK     = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/b796a9d77ebae472_84FCA76B-B21A-492D-9D48-745B3FFA8100.png";
const IMG_TRAIN    = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/3dc2885c221ca3cd_BBFA43BA-51FC-4F46-98F5-CA4803B72BB4.png";
const IMG_EAT      = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/c8450b677c7ba149_CA004476-91AF-42F5-B692-39FE610F5E34.png";
const IMG_LIFE     = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/gcrk1x0k_EC69BB6F-0C15-47A3-974D-16544A44D8E6.png";
const IMG_CONNECT  = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/db18432874cda5bd_bc1838a7-e951-419c-8deb-6594cb64e893.jpg";
const VIDEO_POSTER = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/n5hl4tsq_3aa6a924-5365-43cc-9bf7-c1961e1c39b3.jpg";
const VIDEO_SRC    = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/2qjwvir1_f550f76d-d639-4ec9-8c4b-de987882e146.mp4";

const PILLARS = [
  { key: "walk",    label: "WALK",    icon: Footprints, colour: "#E65322", copy: "Progressively build a sustainable daily walking base and make movement part of normal life." },
  { key: "train",   label: "TRAIN",   icon: Dumbbell,   colour: "#3B82F6", copy: "Complete three full-body resistance sessions per week with Gym and Home options." },
  { key: "eat",     label: "EAT",     icon: Utensils,   colour: "#10B981", copy: "Build a practical whole-food eating pattern without restrictive meal plans." },
  { key: "recover", label: "RECOVER", icon: Moon,       colour: "#7C3AED", copy: "Learn to respect sleep, fatigue and recovery instead of treating harder as always better." },
  { key: "learn",   label: "LEARN",   icon: BookOpen,   colour: "#F2B134", copy: "Short weekly lessons help you understand why the foundations matter and how to keep them going." },
];

const PHASES = [
  { weeks: "WEEKS 1–3",   label: "ESTABLISH",       colour: "#E65322", walk: "3K+",  copy: "Establish your baseline. Learn the foundations and build consistency." },
  { weeks: "WEEKS 4–6",   label: "PROGRESS",        colour: "#3B82F6", walk: "6K+",  copy: "Add reps, load and daily movement without chasing exhaustion." },
  { weeks: "WEEKS 7–9",   label: "PROGRESS HARDER", colour: "#10B981", walk: "9K+",  copy: "Build capability while protecting recovery and keeping the system sustainable." },
  { weeks: "WEEKS 10–12", label: "OWN IT",          colour: "#7C3AED", walk: "10K+", copy: "Create the version of the system you can carry into the next stage of life." },
];

const PORTAL_SHOTS = [
  { src: "/portal-screenshots/dashboard.png", label: "Dashboard" },
  { src: "/portal-screenshots/train.png",     label: "TRAIN" },
  { src: "/portal-screenshots/walk.png",      label: "WALK" },
  { src: "/portal-screenshots/eat.png",       label: "EAT" },
  { src: "/portal-screenshots/module.png",    label: "MODULE" },
  { src: "/portal-screenshots/rebuild.png",   label: "REBUILD SCORE" },
];

const FOR = [
  "People over 30 who care about living healthier for longer.",
  "People who want to remain strong, mobile and independent as they age.",
  "Parents who want more energy and capability now — and want to still be active with future grandchildren.",
  "People who want better body composition without making fitness their entire identity.",
  "People returning to fitness who want to rebuild the basics properly.",
  "People who value simple, sustainable habits more than extreme short-term challenges.",
  "People who want a foundation before exploring more advanced longevity tools or services.",
];

const NOT = [
  "Not a biohacking protocol.", "Not medical longevity treatment.", "Not a 12-week extreme transformation challenge.", "Not a six-day training split.",
  "Not mandatory running or high-impact conditioning.", "Not a restrictive meal plan.", "Not 1:1 coaching.", "Not a promise to extend lifespan or prevent disease.",
];

function Cta({ label = "START BUILDING FOR LONGER", location, testId }) {
  return (
    <Link to="/join" data-testid={testId || `cta-${location}`} data-cta-location={location} data-event="click_join_ba30"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-head uppercase tracking-widest text-sm px-6 py-4 rounded-md hover:opacity-90 transition-opacity">
      {label} <ArrowRight size={16} strokeWidth={2.5} />
    </Link>
  );
}

function Split({ eyebrow, title, body, img, alt, flip = false, dark = false, testId, children }) {
  return (
    <section className={dark ? "bg-foreground text-background" : "bg-card border-y border-border"} data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
        <div className={`md:col-span-5 ${flip ? "md:order-2" : ""}`}>
          <img src={img} alt={alt} loading="lazy" className="w-full aspect-[4/5] object-cover rounded-lg" />
        </div>
        <div className={`md:col-span-7 ${flip ? "md:order-1" : ""}`}>
          <div className={`label-eyebrow ${dark ? "!text-primary" : ""}`}>{eyebrow}</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">{title}</h2>
          <div className={`mt-8 space-y-4 leading-relaxed max-w-2xl ${dark ? "text-background/75" : "text-foreground/80"}`}>{body}</div>
          {children}
        </div>
      </div>
    </section>
  );
}

export default function TenK30() {
  useEffect(() => {
    document.title = "10K30 — The 12-Week Longevity Foundation | BA30 Membership $59/month";
    const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute("content", "10K30 is BA30's mandatory 12-week Longevity Foundation for people over 30: walk more, build strength, eat mostly whole foods, recover, repeat. One $59/month membership.");
    if (!meta.parentElement) document.head.appendChild(meta);
    track("view_10k30_page");
  }, []);

  return (
    <div className="pb-20 md:pb-0" data-testid="tenk30-page">
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border p-3" data-testid="sticky-cta-bar">
        <Link to="/join" data-testid="cta-sticky-mobile" data-event="click_join_ba30"
          className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground font-head uppercase tracking-widest text-sm py-3 rounded-md">
          START 10K30 — $59/MO <ArrowRight size={16} />
        </Link>
      </div>

      {/* 1. HERO */}
      <section className="bg-foreground text-background" data-testid="section-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="label-eyebrow !text-primary">BA30 Membership</div>
            <h1 className="font-display mt-6 text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight" data-testid="hero-headline">
              Build the foundation for <span className="text-primary">the years ahead.</span>
            </h1>
            <p className="mt-6 font-head uppercase text-lg md:text-2xl text-background/90 leading-snug">10K30 — The 12-Week Longevity Foundation for people over 30.</p>
            <div className="mt-6 max-w-2xl space-y-4 text-background/75 leading-relaxed">
              <p>Your health at 50, 60 and 70 is not built overnight. It is shaped by what you repeatedly do now. 10K30 helps people over 30 establish the simple behaviours that support long-term health, strength and physical capability.</p>
              <p className="font-head uppercase tracking-wide text-background">Walk more. Build strength. Eat mostly whole foods. Recover. Repeat.</p>
              <p>Not to turn fitness into your life. To help fitness support the life you want to keep living.</p>
            </div>
            <div className="mt-10"><Cta location="hero" testId="cta-hero" /></div>
          </div>
          <div className="md:col-span-4">
            <div className="rounded-lg border-2 border-primary bg-background/5 p-8" data-testid="hero-price-card">
              <div className="label-eyebrow !text-primary">BA30 Membership</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="wordmark text-6xl md:text-7xl leading-none">$59</span>
                <span className="wordmark text-2xl text-background/70">/ month</span>
              </div>
              <div className="mt-4 text-xs uppercase tracking-widest text-background/60 leading-relaxed">Starts today with 10K30 · Pathways included · No second checkout</div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHY LONGEVITY STARTS WITH THE BASICS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="section-basics">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="label-eyebrow">Why longevity starts with the basics</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Longevity is built in <span className="text-primary">ordinary days.</span></h2>
          </div>
          <div className="md:col-span-5 md:pt-14 space-y-4 text-foreground/80 leading-relaxed">
            <p>You do not need to start with optimisation. Start with the things most people already know matter — but struggle to do consistently.</p>
            <p className="font-head uppercase text-foreground">Move every day. Maintain muscle and strength. Eat well most of the time. Sleep and recover. Stay connected.</p>
            <p>10K30 turns those ideas into a system you can actually follow.</p>
          </div>
        </div>
      </section>

      {/* 3. HEALTHSPAN */}
      <section className="bg-foreground text-background" data-testid="section-healthspan">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20">
          <div className="label-eyebrow !text-primary">What healthspan means</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Not just more years. <span className="text-primary">More good years.</span></h2>
          <p className="mt-8 text-background/75 leading-relaxed max-w-2xl">Lifespan is how long you live. Healthspan is how long you remain healthy, independent and capable. We cannot control everything about how we age — but we can influence many of the behaviours that shape how we arrive there. 10K30 is where those behaviours get built.</p>
        </div>
      </section>

      {/* 4. THE 10K30 FOUNDATIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="section-foundations">
        <div className="label-eyebrow">The 10K30 foundations</div>
        <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Walk. Train. Eat. <span className="text-primary">Recover. Learn.</span></h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PILLARS.map((p) => (
            <div key={p.key} className="bg-card border border-border rounded-lg p-6 lift" style={{ borderTopColor: p.colour, borderTopWidth: 3 }} data-testid={`foundation-${p.key}`}>
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-md" style={{ backgroundColor: p.colour }}><p.icon size={20} color="#fff" strokeWidth={2.25} /></div>
              <div className="wordmark mt-4 text-3xl uppercase">{p.label}</div>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{p.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. 12-WEEK PROGRESSION */}
      <section className="bg-card border-y border-border" data-testid="section-progression">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="label-eyebrow">12-week progression</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Four phases. <span className="text-primary">One system.</span></h2>
          <div className="mt-12 grid md:grid-cols-4 gap-4">
            {PHASES.map((ph, i) => (
              <div key={ph.label} className="rounded-lg p-6 text-white" style={{ backgroundColor: ph.colour }} data-testid={`phase-${i + 1}`}>
                <div className="text-[10px] uppercase tracking-widest opacity-80">{ph.weeks}</div>
                <div className="font-display uppercase text-2xl mt-1 leading-tight">{ph.label}</div>
                <p className="mt-4 text-sm opacity-90 leading-relaxed">{ph.copy}</p>
                <div className="mt-6 text-[10px] uppercase tracking-widest opacity-80">Walk target</div>
                <div className="wordmark text-3xl">{ph.walk}</div>
              </div>
            ))}
          </div>
          <p className="mt-12 font-head uppercase text-xl md:text-3xl leading-snug max-w-3xl" data-testid="twelve-week-promise">
            You do not transform your life in 12 weeks. <span className="text-primary">You build the system that can support it for years.</span>
          </p>
        </div>
      </section>

      {/* 6–9. WALK / STRENGTH / EAT / RECOVER+CONNECT */}
      <Split testId="section-walk" eyebrow="Walking" img={IMG_WALK} alt="Walking outdoors"
        title={<>Keep moving <span className="text-primary">for life.</span></>}
        body={<>
          <p>Walking is not punishment for what you ate. It is one of the simplest ways to keep movement embedded in your day.</p>
          <p>10K30 progressively builds your walking from your current baseline toward the BA30 standard — without pretending 10,000 steps is a magic number.</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground pt-2">Weeks 1–3: 3K+ · Weeks 4–6: 6K+ · Weeks 7–9: 9K+ · Weeks 10–12: 10K+</p>
        </>} />
      <Split testId="section-strength" eyebrow="Strength" img={IMG_TRAIN} alt="Resistance training" flip dark
        title={<>Build strength <span className="text-primary">you can keep.</span></>}
        body={<>
          <p>Muscle and strength become increasingly valuable as you get older. 10K30 gives you three full-body resistance sessions each week to help build a strength habit without turning training into your entire life.</p>
          <p className="font-head uppercase text-background">Gym Track or Home Track. Three sessions. Progressive overload. Quality before exhaustion.</p>
        </>} />
      <Split testId="section-eat" eyebrow="Eat" img={IMG_EAT} alt="Whole-food meal"
        title={<>Eat in a way you can still be doing <span className="text-primary">years from now.</span></>}
        body={<>
          <p>No restrictive meal plan. No banned-food list. No requirement to track every calorie.</p>
          <p>10K30 teaches a predominantly whole-food approach built around protein, smart carbohydrates, fruit, vegetables and repeatable meals. The goal is not perfect eating. The goal is a default way of eating that supports your health, body composition and life.</p>
        </>} />
      <Split testId="section-recover-connect" eyebrow="Recover + connect" img={IMG_CONNECT} alt="Members sharing coffee after a walk" flip dark
        title={<>Health is more <span className="text-primary">than training.</span></>}
        body={<>
          <p>Your body needs recovery. Your life needs people. Sleep, stress, downtime, relationships, community and social connection all belong in a healthy life.</p>
          <p>BA30 is not trying to make fitness your identity. It is helping you build a healthier life around the one you already have.</p>
        </>} />

      {/* Secondary HOW proof — anti-extreme banner kept lower on page */}
      <section className="bg-foreground" data-testid="section-brand-banner">
        <img src={IMG_BANNER} alt="BA30 — Built for life after 30. Move more. Live better. Together." className="block w-full h-auto" fetchpriority="high" loading="eager" data-testid="brand-banner-image" />
      </section>

      {/* 10. BUILT FOR REAL LIFE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="section-real-life">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="label-eyebrow">Built for real life</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">The best longevity system is one you can <span className="text-primary">actually live.</span></h2>
          </div>
          <div className="md:col-span-5 md:pt-14 space-y-4 text-foreground/80 leading-relaxed">
            <p>Work gets busy. Kids need you. You travel. You miss sessions. Weekends happen.</p>
            <p>10K30 teaches you how to keep the foundations alive even when life is not perfect. <span className="font-head uppercase text-foreground">Consistency beats intensity when the time horizon is decades.</span></p>
          </div>
        </div>
        <div className="mt-12">
          <div className="label-eyebrow mb-3">Watch · 10K30 explained in 90 seconds</div>
          <video controls playsInline preload="metadata" poster={VIDEO_POSTER}
                 className="w-full aspect-video rounded-lg bg-black shadow-2xl" data-testid="explainer-video">
            <source src={VIDEO_SRC} type="video/mp4" />
            Your browser can't play this video. <a href={VIDEO_SRC} className="underline">Download it instead.</a>
          </video>
        </div>
      </section>

      {/* 11. AFTER WEEK 12 */}
      <section className="bg-foreground text-background" data-testid="section-after-week-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="label-eyebrow !text-primary">What happens after Week 12</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">The foundation is only <span className="text-primary">the beginning.</span></h2>
          <p className="mt-6 max-w-2xl text-background/75 leading-relaxed">Once you complete 10K30, your BA30 membership continues and you choose the pathway that best matches your next goal.</p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pathway-grid">
            {PATHWAYS.map((p) => (
              <div key={p.id} className="rounded-lg border border-background/15 bg-background/[0.03] p-6" style={{ borderTopColor: p.colour, borderTopWidth: 3 }} data-testid={`pathway-${p.id.toLowerCase()}`}>
                <div className="label-eyebrow" style={{ color: p.colour }}>BA30</div>
                <div className="wordmark mt-1 text-3xl uppercase">{p.id}</div>
                <p className="mt-3 text-sm text-background/70 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 font-head uppercase text-lg md:text-2xl">Different goals. Same objective: <span className="text-primary">stay built for longer.</span></p>
          <p className="mt-6 font-head uppercase text-xl md:text-3xl leading-snug" data-testid="no-new-checkout">No new membership. No second checkout. <span className="text-primary">Just your next pathway.</span></p>
        </div>
      </section>

      {/* 12. PORTAL / REBUILD SCORE */}
      <section className="bg-card border-y border-border" data-testid="section-portal">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="label-eyebrow">Member portal · Rebuild Score</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Track the behaviours that <span className="text-primary">build the result.</span></h2>
          <p className="mt-6 max-w-2xl text-foreground/80 leading-relaxed">The BA30 portal keeps the focus on execution. Your training. Your walking. Your eating habits. Your weekly education. Your Rebuild Score. The goal is not to obsess over every metric. It is to make the important behaviours visible enough to repeat.</p>
          <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {PORTAL_SHOTS.map((s) => (
              <figure key={s.label} className="bg-background border border-border rounded-lg overflow-hidden">
                <img src={s.src} alt={`BA30 Portal — ${s.label}`} loading="lazy" className="w-full aspect-[16/10] object-cover object-top" />
                <figcaption className="p-3"><div className="label-eyebrow !text-primary">{s.label}</div></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 13 + 14. WHO IT'S FOR / WHAT IT'S NOT */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-12" data-testid="section-for-not">
        <div data-testid="section-who-for">
          <div className="label-eyebrow">Who 10K30 is for</div>
          <h2 className="font-display mt-4 text-4xl md:text-5xl uppercase leading-[0.95]">Built for <span className="text-primary">people like you.</span></h2>
          <ul className="mt-8 space-y-3">
            {FOR.map((t) => <li key={t} className="flex gap-3 text-foreground/85"><Check size={16} className="text-primary shrink-0 mt-1" strokeWidth={2.5} />{t}</li>)}
          </ul>
        </div>
        <div className="bg-card border border-border rounded-lg p-8" data-testid="section-not-for">
          <div className="label-eyebrow">What 10K30 is not</div>
          <h2 className="font-display mt-4 text-4xl md:text-5xl uppercase leading-[0.95]">Clear about <span className="text-primary">what we're not.</span></h2>
          <ul className="mt-8 space-y-3">
            {NOT.map((t) => <li key={t} className="flex gap-3 text-foreground/75"><X size={16} className="text-muted-foreground shrink-0 mt-1" strokeWidth={2.5} />{t}</li>)}
          </ul>
        </div>
      </section>

      {/* 15. PRICING */}
      <section className="bg-foreground text-background" data-testid="section-pricing">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
          <div className="label-eyebrow !text-primary">Membership</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Start building <span className="text-primary">for longer</span></h2>
          <div className="mt-12 rounded-lg border-2 border-primary bg-background/5 p-8 md:p-12">
            <div className="flex items-baseline justify-center gap-3">
              <span className="wordmark text-7xl md:text-8xl leading-none" data-testid="pricing-amount">$59</span>
              <span className="wordmark text-2xl md:text-3xl text-background/70">/ month</span>
            </div>
            <p className="mt-8 text-background/80 leading-relaxed max-w-xl mx-auto">Join BA30 and begin with the mandatory 12-week 10K30 Longevity Foundation. Build your movement. Build your strength. Improve how you eat. Learn how to make the foundations fit your life. Then choose your next pathway and keep building.</p>
            <div className="mt-8"><Cta label="START 10K30 — $59/MONTH" location="pricing" testId="cta-pricing" /></div>
            <p className="mt-6 text-[11px] uppercase tracking-widest text-background/50">Recurring monthly · charged at signup · cancel anytime · guided self-service, not 1:1 coaching</p>
          </div>
        </div>
      </section>

      {/* 16. FAQ */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-24" data-testid="section-faq">
        <div className="label-eyebrow">FAQ</div>
        <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Straight <span className="text-primary">answers.</span></h2>
        <div className="mt-10"><FaqList items={TENK30_FAQ} /></div>
      </section>

      {/* 17. FINAL CTA */}
      <section className="bg-foreground text-background relative overflow-hidden" data-testid="section-final-close">
        <img src={IMG_LIFE} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20" loading="lazy" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-28 text-center">
          <div className="label-eyebrow !text-primary">Stronger now. Capable for longer.</div>
          <h2 className="font-display mt-6 text-5xl md:text-7xl uppercase leading-[0.9]">Build now for how you want <span className="text-primary">to live later.</span></h2>
          <div className="mt-10"><Cta location="final" testId="cta-final" /></div>
        </div>
      </section>
    </div>
  );
}
